import { LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { isEowEntry, isManualEntry, isNormalEntry, isSowEntry } from '../../utilities/timesheet-entry.utils';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { ExpenseModel, ODataExpenseOutModel } from '../low-level/expense.model';
import { NotificationIdentifierModel } from '../low-level/notification-identifier.model';
import { TimesheetModel } from '../low-level/timesheet.model';
import { TextModelOptions } from '../options/text-model-options';
import { TimeModelOptions } from '../options/time-model-options';
import { TextModel } from '../primitive/text.model';
import { ArrayModel } from './array.model';
import { CompositeModel } from './composite.model';
import { TIMERECORDING_TYPE_OF_WORK_CODES } from './time-recording-entry.model';
import { TimesheetAddressModel } from './timesheet-address.model';
import { TimesheetEntryInterface } from './timesheet-entry.interface';
import { ODataTimesheetEntryOutModel, TimesheetEntryModel } from './timesheet-entry.model';
import { TimesheetEowModel } from './timesheet-eow.model';
import { TimesheetSowModel } from './timesheet-sow.model';

/**
 * The TimesheetAndExpensesP58Model is bound to the TimesheetAndExpenses component, and submitted to the backend.
 * This model is NOT used for any internal logic that interacts with timesheet data; for this purpose we have a
 * separate TimesheetModel.
 */
export class TimesheetAndExpensesP58Model extends CompositeModel {
    private internalReadonly = false;
    public get readonly(): boolean {
        return this.internalReadonly;
    }

    public set readonly(newValue: boolean) {
        this.internalReadonly = newValue;
        // iterate over timesheet entries and set model options to readonly/disabled
        this.timesheetEntries.value.forEach((entry) => {
            if (isManualEntry(entry) || isSowEntry(entry) || isEowEntry(entry) || isNormalEntry(entry)) {
                entry.startDate = assignModelOptions(
                    entry.startDate,
                    {
                        ...entry.startDate.options,
                        readonly: newValue || (entry instanceof TimesheetEntryModel && !entry.isTravelToEditable),
                    },
                    entry,
                );
                entry.startTime = assignModelOptions(
                    entry.startTime,
                    {
                        ...entry.startTime.options,
                        readonly: newValue || (entry instanceof TimesheetEntryModel && !entry.isTravelToEditable),
                    },
                    entry,
                );
                entry.endDate = assignModelOptions(
                    entry.endDate,
                    {
                        ...entry.endDate.options,
                        readonly: newValue || (entry instanceof TimesheetEntryModel && !entry.isTravelBackEditable),
                    },
                    entry,
                );
                entry.endTime = assignModelOptions(
                    entry.endTime,
                    {
                        ...entry.endTime.options,
                        readonly: newValue || (entry instanceof TimesheetEntryModel && !entry.isTravelBackEditable),
                    },
                    entry,
                );

                // reset min and max values for SoW and EoW in readonly mode - we cannot change them anymore, so no need
                // to display values that have been sent (and were valid back then) as potentially invalid
                if (newValue && !isNormalEntry(entry)) {
                    entry.startDate = assignModelOptions(
                        entry.startDate,
                        {
                            ...entry.startDate.options,
                            minValue: TimeModelOptions.defaultMinValue.clone(),
                        },
                        entry,
                    );
                    entry.startTime = assignModelOptions(
                        entry.startTime,
                        {
                            ...entry.startTime.options,
                            maxValue: TimeModelOptions.defaultMaxValue.clone(),
                        },
                        entry,
                    );
                    entry.endDate = assignModelOptions(
                        entry.endDate,
                        {
                            ...entry.endDate.options,
                            minValue: TimeModelOptions.defaultMinValue.clone(),
                        },
                        entry,
                    );
                    entry.endTime = assignModelOptions(
                        entry.endTime,
                        {
                            ...entry.endTime.options,
                            maxValue: TimeModelOptions.defaultMaxValue.clone(),
                        },
                        entry,
                    );
                }
            }

            if (isManualEntry(entry)) {
                entry.taskTypeDropdown = assignModelOptions(
                    entry.taskTypeDropdown,
                    {
                        ...entry.taskTypeDropdown.options,
                        disabled: newValue,
                    },
                    entry,
                );
            }

            if (isNormalEntry(entry) && entry.meansOfTransport) {
                entry.meansOfTransport = assignModelOptions(
                    entry.meansOfTransport,
                    {
                        ...entry.meansOfTransport.options,
                        disabled: newValue,
                    },
                    entry,
                );
            }

            if (isSowEntry(entry) && entry.startAddressOptions) {
                entry.startAddressOptions = assignModelOptions(
                    entry.startAddressOptions,
                    {
                        ...entry.startAddressOptions.options,
                        readonly: newValue,
                    },
                    entry,
                );
            }

            if (isEowEntry(entry) && entry.endAddressOptions) {
                entry.endAddressOptions = assignModelOptions(
                    entry.endAddressOptions,
                    {
                        ...entry.endAddressOptions.options,
                        readonly: newValue,
                    },
                    entry,
                );
            }
        });
    }

    public timesheetEntries: ArrayModel<TimesheetEntryInterface>;
    public frontId: TextModel;
    public expenses: ArrayModel<ExpenseModel>; // this will contain a flat version of the expenses in expensesDictionary
    public expensesDictionary: { [key: string]: ArrayModel<ExpenseModel> };
    public additionalDistance: TextModel; // model for distance field in additional expenses
    public salesOrg: TextModel;
    public plant: TextModel;
    public startOfWorkLabel = '';
    public displayWorkSchedule: boolean;

    public constructor(
        private log: LogInterface,
        translate: TranslateService,
        modelOptionsLoader: ModelOptionsLoaderService,
        frontId: string,
        private displayTimesheetAddressOptions: boolean,
        timeFormat: string, // clientManager.getTimeFormat();
        currentTimesheet: TimesheetModel,
        submittedTimesheet: TimesheetModel,
    ) {
        super('TimesheetAndExpensesModel');

        // TIME SHEET
        // if there is a start-of-work, we display the current timesheet. Otherwise, the submitted one in readonly mode
        const isCurrentTimesheetToBeDisplayed = currentTimesheet.startOfWork.isValid();
        const timesheetToDisplay = isCurrentTimesheetToBeDisplayed ? currentTimesheet : submittedTimesheet;

        let timesheetEntries: TimesheetEntryInterface[] = [];

        if (timesheetToDisplay.startOfWork.isValid()) {
            // If displaying a submitted timesheet, the previousEndOfWork has to be moment.invalid, because it is not available.
            // We don't remember the second-last submitted timesheet.
            timesheetEntries.push(
                new TimesheetSowModel(
                    timesheetToDisplay.startOfWork,
                    timesheetToDisplay.getFirstEntryDateTime(),
                    isCurrentTimesheetToBeDisplayed ? submittedTimesheet.getEndOfWork() : moment.invalid(),
                    timesheetToDisplay.startAddressValue,
                ),
            );

            timesheetEntries = timesheetEntries.concat(timesheetToDisplay.getTimesheetEntries());

            const endOfWork = timesheetToDisplay.getEndOfWork();

            // Add  EoW model to entries
            timesheetEntries.push(
                new TimesheetEowModel(
                    endOfWork,
                    timesheetToDisplay.getLastEntryDateTime(),
                    timesheetToDisplay.endAddressValue,
                ),
            );
            this.startOfWorkLabel = timesheetToDisplay.startOfWork.isValid()
                ? timesheetToDisplay.startOfWork.format(timeFormat)
                : '';
        }

        this.displayWorkSchedule = false;

        // set  model properties
        this.frontId = <TextModel>this.set(new TextModel('FrontId', frontId));
        this.timesheetEntries = <ArrayModel<TimesheetEntryInterface>>(
            this.set(new ArrayModel<TimesheetEntryInterface>('ActivitySet', timesheetEntries))
        );

        // We can only write to the setter of `this.readonly` once `this.timesheetEntries` has been assigned; otherwise
        // the setter will fail.
        // We need the isReadonly information to control the validation; no current SoW, but timesheet has one
        // --> timesheet was submitted and there is no current one, therefore enable read-only mode
        this.readonly = !currentTimesheet.startOfWork.isValid();

        // EXPENSES (only for P58)
        this.expensesDictionary = {};

        const timesheetNotifications = _.chain(timesheetEntries)
            .filter((entry) => isNormalEntry(entry))
            .map((entry) => entry.notificationId)
            .uniq()
            .value();
        timesheetNotifications.forEach((notificationId) => {
            const timesheetNotification = timesheetToDisplay.notifications.find(
                (n) => n.isConfirmationSubmitted && n.notificationId.equalsTo(notificationId),
            );
            this.expensesDictionary[notificationId.toString()] = new ArrayModel<ExpenseModel>(
                notificationId.toString(),
                timesheetNotification ? timesheetNotification.expenses : [],
            );
        });

        const additionalExpensesNotificationId = new NotificationIdentifierModel(frontId, '', '');
        const additionalNotification = timesheetToDisplay.notifications.find(
            (n) => n.isConfirmationSubmitted && n.notificationId.equalsTo(additionalExpensesNotificationId),
        );
        this.expensesDictionary[additionalExpensesNotificationId.toString()] = new ArrayModel<ExpenseModel>(
            'AdditionalExpenses',
            additionalNotification ? additionalNotification.expenses : [],
        );

        this.additionalDistance =
            additionalNotification && additionalNotification.distance
                ? additionalNotification.distance
                : new TextModel(
                      'Distance',
                      undefined,
                      new TextModelOptions({
                          unit: <string>translate.instant('CONFIRMATION_EXPENSES_KM_LABEL'),
                          inputType: ConfigConstants.inputTypeNumeric,
                      }),
                  );
        modelOptionsLoader.load(this.additionalDistance, 'Timesheet', 'Distance');

        // set  model properties
        this.expenses = <ArrayModel<ExpenseModel>>this.set(new ArrayModel<ExpenseModel>('CseExpenseSet', []));
    }

    public getOutModel(): ODataTimesheetAndExpensesOutModel {
        let sendStartAddress = false;
        // there should be at least one TRTO and one TRBK entry existing in the timesheet besides sow and eow,
        // to be able to send start/end addresses
        const minLengthOfTimesheetEntriesToDisplayAddressOptions = 4;
        if (
            this.displayTimesheetAddressOptions &&
            this.timesheetEntries.value.length > minLengthOfTimesheetEntriesToDisplayAddressOptions
        ) {
            const sow = this.timesheetEntries.value[0];
            // chec k if first entry of timesheet is a TRTO entry, then start address (from) has to be sent
            const firstEntry = this.timesheetEntries.value[1];
            sendStartAddress =
                isNormalEntry(firstEntry) &&
                firstEntry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO &&
                firstEntry.startTime.value.isSame(sow.endTime.value);

            const totalNumberOfEntries = this.timesheetEntries.value.length;
            const eow = <TimesheetEowModel>this.timesheetEntries.value[totalNumberOfEntries - 1];
            // chec k if last entry of timesheet is a TRBK entry, then end address (to) has to be sent
            const two = 2;
            const lastEntry = this.timesheetEntries.value[totalNumberOfEntries - two];
            const sendEndAddress =
                isNormalEntry(lastEntry) &&
                lastEntry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK &&
                lastEntry.endTime.value.isSame(eow.startTime.value);
            if (sendEndAddress) {
                lastEntry.address = new TimesheetAddressModel(
                    '',
                    '',
                    '',
                    '',
                    '',
                    eow.endAddressOptions.selectedEntry.model.value,
                );
            }
        }

        // "thi s.expenses" is a dictionary with the qmnum as key and an ArrayModel as value. There can be as many entries
        // as n otifications have been processed in this timesheet. Merge all "ArrayModel.value" into one array.
        // Addi tional expenses are also contained within the dictionary under a special key.
        const expenses = _.chain(this.expensesDictionary)
            .values()
            .map((expensesArray: ArrayModel<ExpenseModel>) => expensesArray.value)
            .flatten()
            .value();

        // In S AP Gateway Amount or Decimal fields should come with dot (.) as decimal separator, so replace ',' with '.'
        expenses.forEach((expense: ExpenseModel) => {
            expense.expenseValue.value = expense.expenseValue.value.replace(',', '.');
        });
        // upda te the expenses member
        this.expenses.value = expenses;

        // with the changes for CONCUR, add the additional distance value to the first TRTO timesheet entry,
        // if n o TRTO entry exists, then add the additional distance value to the timesheet entry which has a duration greater than 0
        if (this.additionalDistance.value) {
            let additionalDistanceEntry;
            if (this.displayTimesheetAddressOptions) {
                additionalDistanceEntry = this.timesheetEntries.value.find(
                    // firs t TRTO entry
                    (entry) => isNormalEntry(entry) && entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO,
                );
            }

            if (!additionalDistanceEntry) {
                additionalDistanceEntry = this.timesheetEntries.value.find(
                    // posi tive duration entry
                    (entry) =>
                        (isManualEntry(entry) || isNormalEntry(entry)) &&
                        entry.startTime.value.isBefore(entry.endTime.value),
                );
            }

            if (additionalDistanceEntry) {
                if (additionalDistanceEntry.distance) {
                    // if t he entry already contains a distance value, then add the additional-expenses distance value to it
                    // othe rwise only assign the additional-expenses distance value
                    const totalDistance = additionalDistanceEntry.distance.value
                        ? parseInt(additionalDistanceEntry.distance.value, 10) +
                          parseInt(this.additionalDistance.value, 10)
                        : this.additionalDistance.value;

                    additionalDistanceEntry.distance.value = totalDistance.toString();
                } else {
                    additionalDistanceEntry.set(this.additionalDistance);
                }
            } else {
                this.log.warn(
                    'No timesheet entry exists with workcode TRTO or duration greater than 0 to send additional-expenses distance',
                );
            }
        }

        // use  the built-in function to get the correctly named outModel
        const outModel = <ODataTimesheetAndExpensesOutModel>super.getOutModel();
        if (sendStartAddress) {
            outModel.ActivitySet[0].WorkCode = 'SOW';
        }

        // remo ve the first and last entry of the ActivitySet to get rid of the SoW and EoW entries
        outModel.ActivitySet = outModel.ActivitySet.slice(sendStartAddress ? 0 : 1, -1);

        return outModel;
    }
}

export interface ODataTimesheetAndExpensesOutModel extends ODataObject {
    FrontId: string;
    ActivitySet: ODataTimesheetEntryOutModel[];
    CseExpenseSet: ODataExpenseOutModel[];
}
